// src/pages/ClassDetail.jsx
import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import axios from 'axios';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Lottie from 'lottie-react';

import "../styles/ClassDetail.css";
import Loader from '../components/Loader';
import KursplanPage from './Kursplan';
import PartyPopper from '../assets/party-popper.json';
import PartyFace from '../assets/party-face.json';
import ThumbsUp from '../assets/thumbs-up.json';
import RaiseHands from '../assets/raise-hands.json';
import Confetti from '../assets/confetti-balls.json';
import Champagne from '../assets/champagne.json';
import Clap from '../assets/clap.json';
import Cork from '../assets/cork.json';

import { ClassContext } from '../contexts/ClassContext';
import { AusbildungContext } from '../contexts/AusbildungContext';
import { AktionContext } from '../contexts/AktionContext';
import { CombinationContext } from '../contexts/CombinationContext';
import { useKurse } from '../contexts/KurseContext';

const Emojis = [
  { "animation": PartyPopper, "style": { top: "-1rem" } },
  { "animation": Confetti, "style": { top: "0.25rem" } },
  { "animation": PartyFace, "style": { top: "-0.375rem" } },
  { "animation": ThumbsUp, "style": { top: "-1.125rem" } },
  { "animation": RaiseHands, "style": { top: "5px" } },
  { "animation": Clap, "style": { top: "-1rem" } }
];

// -------------------------------------
// helpers
// -------------------------------------
// const getIconUrl = (icon) => {
//   if (!icon || !icon.filename_disk) return null;
//   const baseUrl = process.env.REACT_APP_API_BASE_URL + '/assets/';
//   return `${baseUrl}${icon.filename_disk}`;
// };
const getIconUrl = (icon) => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL + '/assets/';
  if (!icon) return null;

  // Directus file field часто приходит строкой (file id)
  if (typeof icon === 'string') return `${baseUrl}${icon}`;

  // иногда приходит объект
  const fileId = icon.id ?? icon?.filename_disk; // id предпочтительнее
  return fileId ? `${baseUrl}${fileId}` : null;
};

// универсальный ретрай с экспон. бэкоффом
const retry = async (fn, { tries = 4, baseDelay = 300 } = {}) => {
  let lastErr;
  for (let i = 0; i < tries; i++) {
    try {
      return await fn(i);
    } catch (e) {
      lastErr = e;
      const jitter = Math.random() * 0.25 + 0.75; // 0.75–1.0
      const delay = Math.round(baseDelay * Math.pow(2, i) * jitter);
      await new Promise((r) => setTimeout(r, delay));
    }
  }
  throw lastErr;
};

const ClassDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  console.log(id);

  // -------------------------------------
  // контексты
  // -------------------------------------
  const { klassen, loadingKlassen, errorKlassen } = useContext(ClassContext);
  const {
    ausbildungen: allAusbildungen,
    loadingAusbildungen,
    errorAusbildungen,
  } = useContext(AusbildungContext);

  const { aktionen, loadingAktionen, errorAktionen } = useContext(AktionContext);

  const {
    kombinationen,
    fetchKombinationsForClass,
  } = useContext(CombinationContext);

  const { kurse, loadingKurse, errorKurse, fetchKurse } = useKurse();

  // -------------------------------------
  // локальное состояние страницы
  // -------------------------------------

  const [classIdInt, setClassIdInt] = useState(null);

  const [selectedAusbildungId, setSelectedAusbildungId] = useState(null);
  const [selectedAusbildungName, setSelectedAusbildungName] = useState(null);
  const [selectedAusbildungGW, setSelectedAusbildungGW] = useState(false);

  const [openQuestionId, setOpenQuestionId] = useState(null);
  const [showAllQuestions, setShowAllQuestions] = useState(false);
  const [showAllKombis, setShowAllKombis] = useState(false);

  const [kombisForClass, setKombisForClass] = useState([]); // объекты из kombinationen
  const [kombisLoading, setKombisLoading] = useState(true);
  const [kombisError, setKombisError] = useState(null);

  // ленивый кеш деталей Ausbildung (Blocks/Fragen) по id
  const [detailsCache, setDetailsCache] = useState({});
  const abortRef = useRef(null);

  // sticky nav
  const [activeSection, setActiveSection] = useState(null);
  const [hideTitleKlasse, setHideTitleKlasse] = useState(false);

  const randomEmoji = useMemo(() => {
    return Emojis[Math.floor(Math.random() * Emojis.length)]
  }, [selectedAusbildungId]);

  // -------------------------------------
  // derived data
  // -------------------------------------
  const klasseData = useMemo(() => {
    return klassen.find((k) => String(k.slug) === String(id)) || null;
  }, [klassen, id]);

  /* const classIdInt = useMemo(() => {
    return klasseData.id || null;
  }, [klasseData]); */

  console.log(klasseData);
  /* const classIdInt = parseInt(id, 10); */
  /* const classIdInt = isNaN(id) ? id : klasseData.id; */

  const ausbildungen = useMemo(() => {
    return (allAusbildungen || []).filter((a) => {
      const k = a.Klasse?.id ?? a.Klasse ?? a.Klasse_id;
      return String(k) === String(classIdInt);
    });
  }, [allAusbildungen, classIdInt]);

  // -------------------------------------
  // эффекты
  // -------------------------------------

  // 1) fetch всех курсов (один раз на приложение — контекст) при заходе на страницу
  useEffect(() => {
    if (!kurse || kurse.length === 0) {
      fetchKurse();
    }
  }, [kurse, fetchKurse]);

  useEffect(() => {
    if (klassen.length > 0 && !classIdInt) {
      const k0 = klassen.find((k) => String(k.slug) === String(id));
      setClassIdInt(k0.id);
    }
  }, [klassen, classIdInt]);

  // 2) загрузить список комбинаций для класса (только ID), затем сопоставить с полными kombinationen
  useEffect(() => {
    let cancelled = false;
    setKombisLoading(true);
    setKombisError(null);

    retry(() => fetchKombinationsForClass(classIdInt), { tries: 4, baseDelay: 300 })
      .then((kombiIdsOrObjs) => {
        if (cancelled) return;
        const ids = (kombiIdsOrObjs || []).map((k) => k.id ?? k);
        console.log(ids);
        const mapped = ids
          .map((kid) => kombinationen.find((k) => String(k.id) === String(kid)))
          .sort((aKl, bKl) => {
            const a = aKl.Klassen.find((kl) => kl.Klassen_id?.id === classIdInt);
            const b = bKl.Klassen.find((kl) => kl.Klassen_id?.id === classIdInt);
            
            return (a.sort_kombis ?? 0) - (b.sort_kombis ?? 0)
          })
          .filter(Boolean);
        setKombisForClass(mapped);
        console.log(mapped);
      })
      .catch((err) => {
        if (!cancelled) setKombisError(err?.message || "Fehler beim Laden der Kombinationen");
      })
      .finally(() => {
        if (!cancelled) setKombisLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [classIdInt, kombinationen, fetchKombinationsForClass]);

  // 3) выбрать дефолтную Ausbildung
  useEffect(() => {
    if (ausbildungen.length > 0 && !selectedAusbildungId) {
      const a0 = ausbildungen[0];
      setSelectedAusbildungId(a0.id);
      setSelectedAusbildungName(a0.Kurzname || a0.Name);
      setSelectedAusbildungGW(!a0.Grundwissen);
    }
  }, [ausbildungen, selectedAusbildungId]);

  // 4) лениво подгружаем детали выбранной Ausbildung (Blocks/Fragen) с ретраями/абортом
  useEffect(() => {
    const aid = selectedAusbildungId;
    if (!aid) return;

    // уже есть в кеше — ничего не делаем
    if (detailsCache[aid]?.ready) return;

    if (abortRef.current) {
      abortRef.current.abort();
    }
    const ac = new AbortController();
    abortRef.current = ac;

    const loadDetails = async () => {
      const url = `${process.env.REACT_APP_API_BASE_URL}/items/Ausbildungen/${aid}?fields=*,Blocks.*,Blocks.item.*,Blocks.item.Ablauf.*,Blocks.item.Ablauf.item.*,Fragen.*,Fragen.Fragen_id.*`;

      const res = await retry(
        async () => {
          const r = await axios.get(url, { signal: ac.signal });
          return r.data?.data;
        },
        { tries: 4, baseDelay: 300 }
      );

      return res;
    };

    loadDetails()
      .then((data) => {
        if (ac.signal.aborted) return;
        setDetailsCache((prev) => ({
          ...prev,
          [aid]: {
            ready: true,
            blocks: data?.Blocks || [],
            fragen: data?.Fragen || [],
          },
        }));
      })
      .catch((err) => {
        if (!ac.signal.aborted) {
          // помечаем как готово (пусто), чтобы не зависать
          setDetailsCache((prev) => ({
            ...prev,
            [aid]: { ready: true, blocks: [], fragen: [] },
          }));
          console.warn("Fehler beim Laden der Ausbildung-Details:", err);
        }
      });

    /* return () => ac.abort(); */
  }, [selectedAusbildungId, setDetailsCache, detailsCache]);

  // 5) sticky навигация
  useEffect(() => {
    const onScroll = () => {
      const pageYOffset = window.scrollY;
      setHideTitleKlasse(pageYOffset > 0);

      const sections = document.querySelectorAll('[data-section]');
      let newActive = null;
      sections.forEach((section) => {
        const top = section.offsetTop - 220;
        const h = section.offsetHeight;
        if (pageYOffset >= top && pageYOffset < top + h) {
          newActive = section.id;
        }
      });
      setActiveSection(newActive);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // -------------------------------------
  // вычисления для вопросов/блоков
  // -------------------------------------
  const selectedAusbildung = useMemo(
    () => ausbildungen.find((a) => a.id === selectedAusbildungId),
    [ausbildungen, selectedAusbildungId]
  );

  const det = detailsCache[selectedAusbildungId] || { blocks: [], fragen: [], ready: false };

  const websiteAktionen = useMemo(() => {
    return (aktionen || []).filter((ak) =>  ak.channels?.includes("website"))
      /* .filter((ak) => {
        const akid = ak.zuordnung.map
      }) */
  }, [aktionen]);

  const ausbildungAktionen = useMemo(() => {
    if (!selectedAusbildungId) return [];
    return (websiteAktionen || []).filter((ak) => ak.zuordnung_display?.some((akz) => {
      return ((akz.collection === "Ausbildungen") && String(akz.item) === String(selectedAusbildungId))
    }))
  }, [websiteAktionen, selectedAusbildungId]);

  const klasseAktionen = useMemo(() => {
    if (!classIdInt) return [];
    return (websiteAktionen || []).filter((ak) => ak.zuordnung_display?.some((akz) => {
      return ((akz.collection === "Klassen") && String(akz.item) === String(classIdInt))
    }))
  }, [websiteAktionen, classIdInt]);

  console.log(aktionen);
  console.log(ausbildungAktionen);
  console.log(classIdInt);
  console.log(klasseAktionen);

  const sortItems = (items) => [...items].sort((a, b) => (a.sort ?? 9999) - (b.sort ?? 9999));
  const sortedBlocks = useMemo(() => sortItems(det.blocks || []), [det.blocks]);
  const sortedFragen = useMemo(() => sortItems(det.fragen || []), [det.fragen]);
  console.log(sortedFragen);

  const filteredBlocks = useMemo(() => {
    if (!selectedAusbildungId) return [];
    return sortedBlocks.filter((b) => {
      const aid = b.Ausbildungen_id?.id ?? b.Ausbildungen_id ?? b.ausbildung_id;
      return String(aid) === String(selectedAusbildungId);
    });
  }, [sortedBlocks, selectedAusbildungId]);

  const filteredFragen = useMemo(() => {
    if (!selectedAusbildungId) return [];
    return sortedFragen
      .filter((f) => f.Fragen_id?.status === "published")
      .filter((f) => {
          const aid = f.Ausbildungen_id?.id ?? f.Ausbildungen_id ?? f.ausbildung_id;
          return String(aid) === String(selectedAusbildungId);
      });
  }, [sortedFragen, selectedAusbildungId]);

  console.log(filteredFragen);

  const questionsToShow = showAllQuestions ? filteredFragen : filteredFragen.slice(0, 5);
  const hasMoreQuestions = filteredFragen.length > 5;

  // -------------------------------------
  // курсы: фильтрация по классу и Grundwissen
  // -------------------------------------
  const filteredKurse = useMemo(() => {
    let arr = (kurse || []).filter(
      (kurs) => kurs.Kursart !== 'arzt' && kurs.Kursart !== 'ehk'
    );

    if (!selectedAusbildung) return arr;

    if (selectedAusbildung.Grundwissen) {
      arr = arr.filter((kurs) =>
        kurs.Termine?.some((termin) =>
          termin.Modul?.Klassen?.some((k) => k.Klassen_id === classIdInt)
        )
      );
    } else {
      arr = arr.filter((kurs) =>
        kurs.Termine?.some((termin) =>
          termin.Modul?.Klassen?.some((k) => k.Klassen_id === classIdInt) &&
          !String(termin.Modul?.Typ || '').includes('theorie_gw')
        )
      );
    }
    return arr;
  }, [kurse, selectedAusbildung, classIdInt]);

  // -------------------------------------
  // UI вспомогательные
  // -------------------------------------
  const toggleQuestion = (qid) => setOpenQuestionId((prev) => (prev === qid ? null : qid));
  const scrollToSection = (sid) => {
    const el = document.getElementById(sid);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // -------------------------------------
  // ранние возвраты (ГЛОБАЛЬНЫЙ ЛОАДЕР)
  // -------------------------------------
  const detailsReady = selectedAusbildungId ? !!detailsCache[selectedAusbildungId]?.ready : true;

  const loadingCritical =
    loadingKlassen ||
    !classIdInt ||
    loadingAusbildungen ||
    loadingAktionen ||
    loadingKurse ||
    kombisLoading ||
    !klasseData ||
    ausbildungen.length === 0 ||
    !detailsReady;

  const errorAny = errorKlassen || errorAusbildungen || errorAktionen || errorKurse || kombisError;


  if (loadingCritical) {
    return <Loader />; // ← показываем ваш лоадер пока «всё» не готово
  }

  if (errorAny) {
    return <p className="error">{errorAny}</p>;
  }

  if (ausbildungen.length === 0 && !klasseData) {
    return <p>Ausbildung und Klasse sind nicht verfügbar</p>;
  }

  // -------------------------------------
  // render
  // -------------------------------------
  return (
    <>
      <header className="class-header">
        <div className="class-header-container">
          <Link onClick={() => navigate(-1)} /* to=".." */ relative="path" className="back-button">
            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="var(--white)"><path d="m313-440 224 224-57 56-320-320 320-320 57 56-224 224h487v80H313Z" /></svg>
          </Link>
          {klasseData?.Kategorien && (
            <h2 className={hideTitleKlasse ? 'hidden' : ''}>
              { klasseData.Kategorien.find(kat => kat.Kategorien_id.Hauptkategorie === true) ? (
                <Link to={`/klassen/${klasseData.Kategorien.find(kat => kat.Kategorien_id.Hauptkategorie === true).Kategorien_id.Name.toLowerCase()}`}>{klasseData.Kategorien.find(kat => kat.Kategorien_id.Hauptkategorie === true).Kategorien_id.Name}</Link>
              ) : (
                <Link to={`/klassen/${klasseData.Zusatzkategorien.find(kat => kat.Kategorien_id.Hauptkategorie === true).Kategorien_id.Name.toLowerCase()}`}>{klasseData.Zusatzkategorien.find(kat => kat.Kategorien_id.Hauptkategorie === true).Kategorien_id.Name}</Link>
              )}
            </h2>
          )}
          <h1>
            {(klasseData?.Untertitel?.length <= 0 || (klasseData?.Untertitel === null && (klasseData?.Klassenkuerzel || '').length < 6)) && (<span>Klasse</span>)}{' '}
            {klasseData?.Klassenkuerzel}{' '}
            {klasseData?.Untertitel?.length > 0 && (<span>{klasseData.Untertitel}</span>)}
          </h1>
        </div>
      </header>

      <main className="class-detail">
        <div className="ausbildung-main-info">
          <div className="container">
            { klasseData?.Ausbildungsbeginn && (
              <p><span className='class-details-year'>Ausbildungsbeginn</span> ab {klasseData?.Ausbildungsbeginn} Jahre</p>
            )}
            { klasseData?.Pruefungsantritt && (
              <p className='ausbildung-main-info-last-p'><span className='class-details-year'>Prüfungsantritt</span> ab {klasseData?.Pruefungsantritt} Jahre</p>
            )}
            {/* <p style={{ whiteSpace: 'pre-line' }}>{klasseData?.icon_text}</p> */}
            {typeof klasseData?.Icon === 'object' && klasseData?.Icon?.filename_disk && (
              <img
                src={getIconUrl(klasseData.Icon)}
                alt={klasseData.Name}
                className="klasse-detail-icon"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = "/images/placeholder.png";
                }}
              />
            )}
          </div>
        </div>

        <div className="sticky-buttons">
          <nav>
            <button
              type="button"
              className={(activeSection === 'ausbildungen' || activeSection === null) ? 'active' : ''}
              onClick={() => scrollToSection('ausbildungen')}
            >
              {ausbildungen.length > 1 ? 'Varianten' : 'Ausbildung'}
            </button>

            {filteredKurse.length > 0 && (
              <button
                type="button"
                className={activeSection === 'termine' ? 'active' : ''}
                onClick={() => scrollToSection('termine')}
              >
                Kurse
              </button>
            )}

            {kombisForClass.length > 0 && (
              <button
                type="button"
                className={activeSection === 'kombinationen' ? 'active' : ''}
                onClick={() => scrollToSection('kombinationen')}
              >
                Kombis
              </button>
            )}
          </nav>
        </div>

        <section id="ausbildungen" data-section>
          <div className="ausbildung-list container">
            {ausbildungen.length > 1 && <p>Unsere Ausbildungsvarianten:</p>}
            <ul>
              {ausbildungen.map((a) => (
                <li key={a.id} className="ausbildung-item">
                  <div className="ausbildung-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="35" height="35">
                      <g transform="rotate(-90 12 12)">
                        <path d="M12 2v12" stroke="rgb(22, 160, 211)" strokeWidth="3" fill="none" />
                        <path d="M12 16l-6-6h12l-6 6z" fill="rgb(22, 160, 211)" />
                      </g>
                    </svg>
                  </div>
                  <div>
                    <h3 className="ausbildung-name">{a.Name}</h3>
                    {a.Kurzbeschreibung && <p style={{ margin: 0 }}>{a.Kurzbeschreibung}</p>}
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {ausbildungen.length > 1 && (
            <div className="ausbildung-selector">
              <div className="ausbildung-selector-buttons">
                <div className="button-group">
                  {ausbildungen.map((a) => (
                    <a
                      key={a.id}
                      className={`orange-select ${selectedAusbildungId === a.id ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedAusbildungId(a.id);
                        setSelectedAusbildungName(a.Kurzname || a.Name);
                        setSelectedAusbildungGW(!a.Grundwissen);
                      }}
                    >
                      {a.Kurzname ? a.Kurzname : a.Name}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          )}

          {(ausbildungAktionen.length > 0) && ( 
            <div className="aktion-container">
              <div className="aktion">
                <div className="aktion-content">
                  <h2>{ausbildungAktionen[0].title}</h2>
                  <p>{ausbildungAktionen[0].text}</p>
                </div>
                {ausbildungAktionen[0].rabatt && (
                  <div className="aktion-rabatt">
                    <span>Jetzt</span>
                    {ausbildungAktionen[0].rabatt % 1 === 0 ? (
                      <span className="aktion-rabatt-price">
                        € <strong>{Math.trunc(ausbildungAktionen[0].rabatt)}</strong>,–
                      </span>
                    ) : (
                      <span className="aktion-rabatt-price">
                        € <strong>{Math.trunc(ausbildungAktionen[0].rabatt)}</strong>,{ausbildungAktionen[0].rabatt.toString().split('.')[1]}
                      </span>
                    )}
                    <span>sparen!</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {(ausbildungAktionen.length <= 0) && (klasseAktionen.length > 0) && ( 
            <div className="aktion-container">
              <div className="aktion">
                <div className="aktion-content">
                  <h2>{klasseAktionen[0].title}</h2>
                  <p>{klasseAktionen[0].text}</p>
                </div>
                {klasseAktionen[0].rabatt && (
                  <div className="aktion-rabatt">
                    <span>Jetzt</span>
                    {klasseAktionen[0].rabatt % 1 === 0 ? (
                      <span className="aktion-rabatt-price">
                        € <strong>{Math.trunc(klasseAktionen[0].rabatt)}</strong>,–
                      </span>
                    ) : (
                      <span className="aktion-rabatt-price">
                        € <strong>{Math.trunc(klasseAktionen[0].rabatt)}</strong>,{klasseAktionen[0].rabatt.toString().split('.')[1]}
                      </span>
                    )}
                    <span>sparen!</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {selectedAusbildungId && (
            <div className="ausbildung-details container">
              {ausbildungen
                .filter((a) => a.id === selectedAusbildungId)
                .map((a) => (
                  <div key={a.id} className="ausbildung-header">
                    <div>
                      <h2>{a.Name}</h2>
                      {a.Kurzbeschreibung && (
                        <div
                          className="klasse-detail-description"
                          dangerouslySetInnerHTML={{ __html: a.Kurzbeschreibung }}
                        />
                      )}
                    </div>

                    {klasseData?.Externer_Link ? (
                      <a className="button pagelink" href={klasseData.Externer_Link} target="_blank" rel="noreferrer">
                        { klasseData.Externer_Link.includes('mailto:') ? 
                          'Anfrage per E-Mail senden' :
                          klasseData.Externer_Link.includes('tel:') ?
                            'Per Telefon anfragen' : 'Zur Website' }
                      </a>
                    ) : (
                      <Link className="button pagelink" to={`/angebot/${a.id}`}>Angebot anfordern</Link>
                    )}
                  </div>
                ))}

              {/* Blocks */}
              {filteredBlocks.length > 0 && (
                <div className="klasse-detail-blocks">
                  {filteredBlocks.map((block) => (
                    <React.Fragment key={block.id}>
                      {block.collection === "Ausbildungsverlauf" ? (
                        <div className="verlauf-block">
                          {block.item?.Headline && <h3>{block.item.Headline}</h3>}
                          {block.item?.Content && (
                            <div
                              className="verlauf-block-content"
                              dangerouslySetInnerHTML={{ __html: block.item.Content }}
                            />
                          )}
                          <div className="ablauf-container">
                            {(block.item?.Ablauf || []).map((ablauf) => (
                              <div key={ablauf.id} className={`ablauf-block block-${ablauf.collection}`}>
                                {ablauf.item?.Headline && <h4>{ablauf.item.Headline}</h4>}
                                {ablauf.item?.Content && (
                                  <div
                                    className="ablauf-block-content"
                                    dangerouslySetInnerHTML={{ __html: ablauf.item.Content }}
                                  />
                                )}
                                {ablauf.collection === "Ausbildungsende" && (
                                  <Lottie animationData={randomEmoji.animation} style={randomEmoji.style} autoPlay loop />
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="klasse-block">
                          {block.item?.Headline && <h4>{block.item.Headline}</h4>}
                          {block.item?.Content && (
                            <div
                              className="klasse-block-content"
                              dangerouslySetInnerHTML={{ __html: block.item.Content }}
                            />
                          )}
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              )}

              {/* Fragen */}
              {filteredFragen.length > 0 && selectedAusbildung && (
                <div className="klasse-detail-fragen">
                  <h2>
                    Alles Wissenswerte zu <br />
                    <span className='spanBlau'>Klasse {selectedAusbildung.Name}</span>
                  </h2>

                  {questionsToShow.map((fr) => {
                    const frageId = fr.id;
                    const frageObj = fr.Fragen_id;
                    const isOpen = openQuestionId === frageId;
                    if (!frageObj) return null;

                    return (
                      <div key={frageId} className={`klasse-frage ${isOpen ? 'open' : ''}`}>
                        <div
                          className="frage-header"
                          onClick={() => toggleQuestion(frageId)}
                          aria-expanded={isOpen}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') toggleQuestion(frageId);
                          }}
                        >
                          <div className="frage-content">
                            <span className="frage-text">{frageObj.Frage}</span>
                          </div>
                          <div className={`checkmark ${isOpen ? 'open' : ''}`}>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960"><path d="m480-453-95-95q-11-11-27.5-11T329-548q-12 12-12 28.5t12 28.5l123 123q12 12 28 12t28-12l124-124q12-12 11.5-28T631-548q-12-11-28-11.5T575-548l-95 95Zm0 373q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z" /></svg>
                          </div>
                        </div>
                        <div className={`klasse-antwort ${isOpen ? 'open' : ''}`}>
                          {frageObj.Antwort && (
                            <div
                              dangerouslySetInnerHTML={{ __html: frageObj.Antwort }}
                              className="antwort-content"
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {hasMoreQuestions && !showAllQuestions && (
                    <button className="alle-anzeigen-button" onClick={() => setShowAllQuestions(true)}>
                      Alle anzeigen
                    </button>
                  )}
                  {hasMoreQuestions && showAllQuestions && (
                    <button className="alle-anzeigen-button" onClick={() => setShowAllQuestions(false)}>
                      Weniger anzeigen
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Zusatzausbildungen */}
          {Array.isArray(klasseData?.Zusatzausbildungen) && klasseData.Zusatzausbildungen.length > 0 && (
            <div className="zusatz-section class-detail-zusatz">
              <div className="container">
                <h3 className='zusatz-anhaenger-class'>Zusatzausbildungen</h3>
                <div className="cards3">
                  {klasseData.Zusatzausbildungen.map((kl) => (
                    /* { klasseData.Kategorien.find(kat => kat.Kategorien_id.Hauptkategorie === true) ? (
                      <Link to={`/klassen/${klasseData.Kategorien.find(kat => kat.Kategorien_id.Hauptkategorie === true).Kategorien_id.Name.toLowerCase()}`}>{klasseData.Kategorien.find(kat => kat.Kategorien_id.Hauptkategorie === true).Kategorien_id.Name}</Link>
                    ) : (
                      <Link to={`/klassen/${klasseData.Zusatzkategorien.find(kat => kat.Kategorien_id.Hauptkategorie === true).Kategorien_id.Name.toLowerCase()}`}>{klasseData.Zusatzkategorien.find(kat => kat.Kategorien_id.Hauptkategorie === true).Kategorien_id.Name}</Link>
                    )} */
                    <a
                      href={`/klassen/detail/${kl.related_Klassen_id.slug}`}
                      key={kl.related_Klassen_id.id}
                      className="card3 card-link"
                    >
                      <hgroup>
                        <div>
{(() => {
  const iconUrl = getIconUrl(kl.related_Klassen_id.Icon);

  return iconUrl ? (
    <div className="klasse-icon">
      <img
        src={iconUrl}
        alt={kl.related_Klassen_id.Name}
        loading="lazy"
        onError={(e) => {
          e.currentTarget.onerror = null;
          e.currentTarget.src = "/images/placeholder.png";
        }}
      />
    </div>
  ) : (
    <div>No Icon</div>
  );
})()}

                          { kl.related_Klassen_id.Klassenkuerzel ? <h3>{kl.related_Klassen_id.Klassenkuerzel}</h3> : ''}
                        </div>
                        { kl.related_Klassen_id.Untertitel ? <p>{kl.related_Klassen_id.Untertitel}</p> : ''}
                      </hgroup>
                      <p>{kl.related_Klassen_id.icon_text}</p>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Termine / Kurse */}
          { filteredKurse.length > 0 && (
            <section id="termine" data-section>
              <div className="termine-container">
                <div style={{ display: "flex", alignItems: "center" }}>
                  <h2 style={{ marginInline: "1.5rem" }}>Kurse für {klasseData?.Name}</h2>
                  <p>(Variante: {selectedAusbildungName})</p>
                </div>
              </div>
            
              <KursplanPage
                key={selectedAusbildungGW}
                smallKursplan={true}
                prefilter={true}
                selectedClasses={[klasseData?.id]}
                grundwissen={selectedAusbildungGW}
              />
            </section>
          )}
        </section>

        {/* Kombis */}
        {kombisForClass.length > 0 && (
          <section className="kombinationen-section" id="kombinationen" data-section>
            <div className="container">
              <h2>Kombis mit {klasseData?.Name}</h2>
              <div className="cards2-margin">
                {(showAllKombis ? kombisForClass : kombisForClass
                  .slice(0, 4))
                  .map((kombi) => {
                    if (!kombi) return null;
                    return (
                      <Link
                        to={`/kombi/${kombi.id}`}
                        key={kombi.id}
                        className="card2-combi-class"
                        style={{ textDecoration: 'none', color: 'inherit' }}
                      >
                        <h3>{kombi.Name || 'Kein Name verfügbar'}</h3>
                        <div className="icons-container">
                          {Array.isArray(kombi.Klassen) && kombi.Klassen.length > 0 ? (
                            (() => {
                              const seen = new Set();
                              const unique = kombi.Klassen
                                .sort((a, b) => (a.sort_klassen ?? 0) - (b.sort_klassen ?? 0))
                                .filter((k) => {
                                  const iconId = k.Klassen_id?.Icon;
                                  if (!iconId || seen.has(iconId)) return false;
                                  seen.add(iconId);
                                  return true;
                                });

                              return unique.length > 0 ? (
                                unique.map((k, idx) => {
                                  const iconId = k.Klassen_id?.Icon;
                                  const iconUrl = iconId
                                    ? `${process.env.REACT_APP_API_BASE_URL}/assets/${iconId}`
                                    : null;
                                  const kid = k.Klassen_id?.id || `klasse-${idx}`;
                                  return iconUrl ? (
                                    <img
                                      key={`${kid}-${idx}`}
                                      src={iconUrl}
                                      alt={k.Klassen_id?.Name || 'Kein Name'}
                                      className="klasse-icon-combi"
                                      loading="lazy"
                                      onError={(e) => {
                                        e.currentTarget.onerror = null;
                                        e.currentTarget.src = '/images/placeholder.png';
                                      }}
                                    />
                                  ) : (
                                    <div key={`${kid}-${idx}`} className="klasse-icon-placeholder">No Icon</div>
                                  );
                                })
                              ) : (
                                <div className="no-icons">Keine Icons verfügbar</div>
                              );
                            })()
                          ) : (
                            <div className="no-icons">Keine Icons verfügbar</div>
                          )}
                        </div>
                      </Link>
                    );
                })}
              </div>

              {kombisForClass.length > 4 && (
                <div className="kombis-toggle-button">
                  <button className="button pagelink" onClick={() => setShowAllKombis((p) => !p)}>
                    {showAllKombis ? 'Weniger anzeigen' : 'Mehr anzeigen'}
                  </button>
                </div>
              )}
            </div>
          </section>
        )}

        {kombisError && (
          <div className="container">
            <p className="error" style={{ marginTop: 16 }}>{kombisError}</p>
          </div>
        )}

        {/* Gesetzliche Bestimmungen */}
        {klasseData?.Beschreibung && (
          <section id="rechtliches" data-section>
            <div className="klasse-detail-class-description container">
              <h4 className='description'>Gesetzliche Bestimmungen:</h4>
              <div dangerouslySetInnerHTML={{ __html: klasseData.Beschreibung }} />
            </div>
          </section>
        )}
      </main>
    </>
  );
};

export default ClassDetail;
